import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { workersClient } from "@/integrations/database/client";
import { useQuery } from "@tanstack/react-query";

// Feature flags interface
export interface FeatureFlags {
  enable_attachments: boolean;
  enable_offer_editing: boolean;
  enable_offer_bumping: boolean;
  enable_public_profiles: boolean;
  enable_profile_screenshots: boolean;
  enable_referral_system: boolean;
  enable_analytics_dashboard: boolean;
}

// Configuration context type
interface ConfigurationContextType {
  featureFlags: FeatureFlags | null;
  isLoading: boolean;
  error: Error | null;
  refetchConfig: () => Promise<void>;
}

// Default feature flags (fallback values)
const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  enable_attachments: true,
  enable_offer_editing: true,
  enable_offer_bumping: true,
  enable_public_profiles: true,
  enable_profile_screenshots: true,
  enable_referral_system: true,
  enable_analytics_dashboard: true,
};

// Create context with default values
const ConfigurationContext = createContext<ConfigurationContextType>({
  featureFlags: DEFAULT_FEATURE_FLAGS,
  isLoading: false,
  error: null,
  refetchConfig: async () => {},
});

// Hook to use configuration
export const useConfig = () => {
  const context = useContext(ConfigurationContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigurationProvider");
  }
  return context;
};

// Fetch configuration from D1 Database
const fetchConfiguration = async (): Promise<FeatureFlags> => {
  try {
    // Determine environment (default to production)
    const environment =
      import.meta.env.MODE === "development" ? "development" : "production";

    // Call RPC function to get config
    const { data, error } = await workersClient.rpc("get_config", {
      p_environment: environment,
      p_key: "feature_flags",
    });

    if (error) {
      console.error("Error fetching configuration:", error);
      return DEFAULT_FEATURE_FLAGS;
    }

    // Parse the JSON response
    if (data && typeof data === "object" && !Array.isArray(data)) {
      return {
        ...DEFAULT_FEATURE_FLAGS,
        ...(data as Record<string, unknown>),
      } as FeatureFlags;
    }

    return DEFAULT_FEATURE_FLAGS;
  } catch (err) {
    console.error("Unexpected error fetching configuration:", err);
    return DEFAULT_FEATURE_FLAGS;
  }
};

// Configuration Provider Component
export const ConfigurationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags | null>(null);

  // 🔥 CRITICAL FIX: Import useAuth to check authentication status
  // This prevents premature API calls that cause 401 errors during initial auth
  // We need to import it at the top of the file, but for now we'll use localStorage
  const hasAuthToken = () => {
    return !!localStorage.getItem('workers_access_token');
  };

  // Use TanStack Query for configuration fetching with caching
  const {
    data: configData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["app-configuration"],
    queryFn: fetchConfiguration,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    // OPTIMIZATION: Disabled refetchOnWindowFocus - config rarely changes
    // This prevents unnecessary API calls on every tab switch
    refetchOnWindowFocus: false,
    retry: 3,
    // 🔥 CRITICAL FIX: Don't fetch config until we have an auth token
    // This prevents 401 errors during initial authentication flow
    enabled: hasAuthToken(),
  });

  // Update local state when data changes
  useEffect(() => {
    if (configData) {
      setFeatureFlags(configData);
    }
  }, [configData]);

  // Refetch function for manual updates
  const refetchConfig = async () => {
    await refetch();
  };

  const value: ConfigurationContextType = {
    featureFlags,
    isLoading,
    error: error as Error | null,
    refetchConfig,
  };

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
};

export default ConfigurationContext;
